'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Skeleton } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useEffect, useState } from 'react';
import { Image } from '@/components/client';
import { CUSTOM_SCHEME } from '@/modules/wello-app';
import { formatPrice } from '@/utils/formatPrice';
import { useBenefitGate } from '../../_hooks/useBenefitGate';
import { useClaimCoupang } from '../_hooks/useCoupangEarn';
import type { CoupangRecommendProduct } from '../_hooks/useCoupangRecommend';

//* 적립 가능하면 지금 누를 상품 하나에 집중시키고, 쿨타임 동안은 둘러볼 4개를 편다.
//* 더줌 가이드도 분당 요청량 제한 때문에 "받은 리스트에서 하나씩" 노출을 권한다.
const resolveVisibleCount = (isAvailable: boolean) => isAvailable ? 1 : 4;
const SectionFrame = t0 => {
  const $ = _c(5);
  const {
    visibleCount,
    children
  } = t0;
  const t1 = visibleCount === 1 ? "grid-cols-1" : "grid-cols-2";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("grid w-full gap-8", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <ul className={t2}>{children}</ul>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
interface CoupangRecommendSkeletonProps {
  isAvailable: boolean;
}

//* 광고ID 왕복 뒤에야 페치되는 늦은 콘텐츠 — 실제와 같은 칸 수로 공간을 예약해
//* 목록 등장 순간 위 문구가 밀리지 않게 한다.
export const CoupangRecommendSkeleton = t0 => {
  const $ = _c(2);
  const {
    isAvailable
  } = t0;
  let t1;
  if ($[0] !== isAvailable) {
    const visibleCount = resolveVisibleCount(isAvailable);
    t1 = <SectionFrame visibleCount={visibleCount}>{Array.from({
        length: visibleCount
      }, _temp)}</SectionFrame>;
    $[0] = isAvailable;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface CoupangRecommendSectionProps {
  products: CoupangRecommendProduct[];
  isAvailable: boolean;
}
export const CoupangRecommendSection = t0 => {
  const $ = _c(27);
  const {
    products,
    isAvailable
  } = t0;
  const {
    mutate: claim
  } = useClaimCoupang();
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  let t1;
  if ($[0] !== products.length) {
    t1 = () => Math.floor(Math.random() * products.length);
    $[0] = products.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [offset, setOffset] = useState(t1);
  let t2;
  if ($[2] !== isAvailable) {
    t2 = resolveVisibleCount(isAvailable);
    $[2] = isAvailable;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const visibleCount = Math.min(t2, products.length);
  const visibleProducts = Array.from({
    length: visibleCount
  }, (_, index) => products[(offset + index) % products.length]);
  let t3;
  if ($[4] !== products.length || $[5] !== visibleCount) {
    t3 = () => setOffset(previous => (previous + visibleCount) % products.length);
    $[4] = products.length;
    $[5] = visibleCount;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleClickShuffle = t3;
  let t4;
  let t5;
  if ($[7] !== offset || $[8] !== products || $[9] !== visibleCount) {
    t4 = () => {
      for (let index_0 = 0; index_0 < visibleCount; index_0 = index_0 + 1, index_0) {
        const preloaded = new window.Image();
        preloaded.src = products[(offset + visibleCount + index_0) % products.length].productImage;
      }
    };
    t5 = [offset, visibleCount, products];
    $[7] = offset;
    $[8] = products;
    $[9] = visibleCount;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  const handleClickProduct = clickUrl => () => {
    triggerGate(["app", "login", "consent"], () => {
      window.location.href = `${CUSTOM_SCHEME.EXTERNAL_PREFIX}${clickUrl}`;
      if (isAvailable) {
        claim({});
      }
    });
  };
  const t6 = "flex w-full flex-col items-center gap-12";
  const T0 = SectionFrame;
  const t7 = visibleProducts.map(product => <li key={product.clickUrl}><button onClick={handleClickProduct(product.clickUrl)} className={cn("flex w-full flex-col gap-8 rounded-small p-8 text-left", INTERACTIVE_SURFACE)}><div className="relative aspect-square w-full overflow-hidden rounded-small bg-Gray-50"><Image src={product.productImage} alt={product.productName} fill={true} sizes="(max-width: 750px) 50vw, 360px" className="object-cover" /></div><p className="line-clamp-2 font-500 text-Gray-900 typo-body3">{product.productName}</p><div className="flex items-center gap-8"><span className="font-700 text-Gray-900 typo-body1">{formatPrice(product.productPrice)}</span>{product.isRocket ? <Badge className="bg-B-50 text-B-500">로켓배송</Badge> : null}</div></button></li>);
  let t8;
  if ($[12] !== T0 || $[13] !== t7 || $[14] !== visibleCount) {
    t8 = <T0 visibleCount={visibleCount}>{t7}</T0>;
    $[12] = T0;
    $[13] = t7;
    $[14] = visibleCount;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== handleClickShuffle || $[17] !== isAvailable || $[18] !== products.length || $[19] !== visibleCount) {
    t9 = !isAvailable && products.length > visibleCount ? <Button theme="sub-text" dimensions="H40" onClick={handleClickShuffle}>다른 상품 보기</Button> : null;
    $[16] = handleClickShuffle;
    $[17] = isAvailable;
    $[18] = products.length;
    $[19] = visibleCount;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className={t6}>{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== gateModals || $[25] !== t10) {
    t11 = <>{t10}{gateModals}</>;
    $[24] = gateModals;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
function _temp(_, index) {
  return <li key={index} className="flex flex-col gap-8 p-8"><Skeleton className="aspect-square w-full rounded-small" /><Skeleton className="w-full rounded-xsmall" typo="body3" /><Skeleton className="w-80 rounded-xsmall" typo="body1" /></li>;
}